import { GraphQLResult } from '@aws-amplify/api-graphql';
import { makeAutoObservable } from 'mobx';
import {
  getAttendeesByFilter,
  IAttendee,
  IGetAttendeePayload,
} from '../../api/dao/attendee-dao';
import { GetAttendeesQuery } from '../../graphql/API';
import { IGraphQLErrorResponse } from '../../utils/services/error-parsing-service';
import ErrorBoundaryStore from '../error-boundary-store/error-boundary-store';
import { getErrorBoundaryStore } from '../error-boundary-store/error-boundary-store-instanse';
import { PagingStore } from '../paging-store/paging-store';
import PodiumInfoStore from '../podium-info-store/podium-info-store';
import { getPodiumInfoStore } from '../podium-info-store/podium-info-store-instance';
import { AttendeesService } from './attendees-service/attendees-service';
import { getAttendessService } from './attendees-service/attendees-service-instance';
import * as mobx from 'mobx';
import { AttendeeStatus } from '../../constans/attendee-enums';
import { IReuqestFiltersParams, ONE } from '../../constans/common-constants';
import { INTERVAL } from '../../constans/attendees-constants';
import { getActivityAttendeeCountService } from './activity-attendee-count-service/activity-attendee-count-service-instance';
import { ActivityAttendeeCountService } from './activity-attendee-count-service/activity-attendee-count-service';
import { RaceConditionGuard } from '../../utils/helpers/race-condition-guard';
import { AttendeeConnectionControlService } from './attendee-connection-control-service/attendee-connection-control-service';
import { getAttendeeConnectionControlService } from './attendee-connection-control-service/attendee-connection-control-service-instance';

interface IAttendeesResponse extends IAttendeesRequest {
  attendees: IAttendee[];
  totalAttendees: number;
}

interface IAttendeesRequest extends IReuqestFiltersParams {
  filterStatus: AttendeeStatus;
}

class AttendeesStore {
  private pagingStore: PagingStore;
  private pollingInterval: NodeJS.Timeout;
  private isLoading: boolean;

  constructor (
    private readonly errorBoundary: ErrorBoundaryStore = getErrorBoundaryStore(),
    private readonly podiumInfoStore: PodiumInfoStore = getPodiumInfoStore(),
    private readonly attendeesService: AttendeesService = getAttendessService(),
    private readonly activityAttendeeCountService: ActivityAttendeeCountService = getActivityAttendeeCountService(),
    private readonly raceConditionGuard: RaceConditionGuard = new RaceConditionGuard(),
    private readonly attendeeConnectionControlService: AttendeeConnectionControlService = getAttendeeConnectionControlService()
  ) {
    makeAutoObservable(this);
    this.pagingStore = new PagingStore();
    this.isLoading = false;
    mobx.reaction(() => this.attendeesService.sortDirectionValue, this.getAttendeesByFilter);
    mobx.reaction(() => this.attendeesService.activeSortFieldParam, this.getAttendeesByFilter);
    mobx.reaction(() => this.attendeesService.isSearch, (isSearch: boolean) => this.onEndSearchingReaction(isSearch));
    mobx.reaction(() => this.attendeesService.activityFilters, this.getAttendeesByFilter);
  }

  public setIsLoading = (value: boolean): void => {
    this.isLoading = value;
  };

  public getIsLoading = (): boolean => {
    return this.isLoading;
  };

  public getAttendeesService = (): AttendeesService => {
    return this.attendeesService;
  };

  public getActivityAttendeeCountService = (): ActivityAttendeeCountService => {
    return this.activityAttendeeCountService;
  };

  public getPagingStore = (): PagingStore => {
    return this.pagingStore;
  };

  public getAttendeeConnectionControlService = (): AttendeeConnectionControlService => {
    return this.attendeeConnectionControlService;
  };

  public getAttendeesByFilter = async (): Promise<void> => {
    const presentationId: string | null = this.podiumInfoStore.getPresentationId();

    if (!presentationId) return;

    const payload: IGetAttendeePayload = this.generateAttendeesPayloadWithFilters();

    try {
      const response: GraphQLResult<GetAttendeesQuery> = await this.raceConditionGuard.getGuardedPromise(getAttendeesByFilter(payload));
      const parsedResponse: IAttendeesResponse = JSON.parse(response.data?.getAttendees as string);

      if(parsedResponse.attendees) {
        const { attendees, totalAttendees, page } = parsedResponse;
        this.attendeesService.initAttendees(attendees as IAttendee[]);
        this.pagingStore.setTotalRecords(totalAttendees);
        this.setPages(page);
      }

    } catch(error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
      this.clearPollingInterval();
    } finally {
      this.setIsLoading(false);
    }
  };

  public getAttendeesInterval = (): void => {
    this.getAttendeesByFilter();
    this.pollingInterval = setInterval(() => {
      this.getAttendeesByFilter();
    }, INTERVAL);
  };

  public searchCallbackBuilder = (): void => {
    this.pagingStore.selectPage(ONE);
    this.setIsLoading(true);
    this.getAttendeesByFilter();
  };

  public clearPollingInterval = (): void => {
    clearInterval(this.pollingInterval);
  };

  private onEndSearchingReaction = (isSearch: boolean): void => {
    if(isSearch) return;
    this.searchCallbackBuilder();
  };

  private generateAttendeesPayloadWithFilters = (): IGetAttendeePayload => {
    return {
      size: this.pagingStore.getPageSize(),
      page: this.pagingStore.getCurrentPage(),
      query: this.attendeesService.searchQueryString,
      sort: {
        name: this.attendeesService.activeSortFieldParam,
        direction: this.attendeesService.sortDirectionValue,
      },
      filterStatus:  this.attendeesService.activityFilters,
      presentationId: this.podiumInfoStore.getPresentationId()!,
    };
  };

  private setPages = (page: number): void => {
    if (this.pagingStore.getCurrentPage() > page) {
      this.pagingStore.selectPage(page);
    }

    if(this.pagingStore.getTotalPages() <= ONE) {
      this.pagingStore.selectPage(ONE);
    }
  };
}

export default AttendeesStore;